import { useCallback } from 'react';
import { TCHOP_ROOT_URL } from '../../env';
import { prepareHeaders } from '../../services';
import { getOrganisationDomain } from '../utils';

const PAYLOAD_TOO_LARGE_STATUS = 413;

export enum FileAcceptType {
  IMAGE = 'image/*',
  AUDIO = '.mp3,.wav,.aiff',
  VIDEO = 'video/*',
  PDF = '.pdf',
  FILE = '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.odt,.ods,.odp',
}

export enum UploadType {
  IMAGE = 'image',
  AUDIO = 'audio',
  VIDEO = 'video',
  PDF = 'pdf',
  FILE = 'file',
}

export enum ImageUploadType {
  PROFILE = 'profile',
  ITEM = 'item',
  COMMENT = 'comment',
  STORY = 'story',
}

export interface UploadHandlerArgs {
  file: File;
  type: UploadType;
  imageUploadType?: ImageUploadType;
  onProgress?: (progress: number) => void;
}

interface UploadCompletedArgs {
  assemblyId: string;
  callback?: () => void;
}

interface UploadResult {
  id: number;
  assemblyId: string;
  isFileTooLarge: boolean;
}

export const useUpload = () => {
  const onUploadCompleted = useCallback(({ assemblyId, callback }: UploadCompletedArgs) => {
    if (!assemblyId) {
      return;
    }

    const interval = setInterval(async () => {
      const { status } = await fetch(
        `${TCHOP_ROOT_URL}/api/fs/assembly/${assemblyId}/status?organisation=${getOrganisationDomain()}`,
        { credentials: 'include', headers: prepareHeaders(new Headers()) }
      ).then((result) => result.json());

      if (status === 'ok') {
        callback?.();
        clearInterval(interval);
      }
    }, 1500);
  }, []);

  const getUploadType = useCallback(({ acceptType }: { acceptType: FileAcceptType }) => {
    switch (acceptType) {
      case FileAcceptType.AUDIO:
        return UploadType.AUDIO;
      case FileAcceptType.VIDEO:
        return UploadType.VIDEO;
      case FileAcceptType.PDF:
        return UploadType.PDF;
      case FileAcceptType.FILE:
        return UploadType.FILE;
      default:
        return UploadType.IMAGE;
    }
  }, []);

  const uploadHandler = useCallback(
    ({ file, type, imageUploadType, onProgress }: UploadHandlerArgs) =>
      new Promise<UploadResult>((resolve) => {
        const req = new XMLHttpRequest();

        const resolveWithFailure = () => {
          resolve({
            id: 0,
            assemblyId: '',
            isFileTooLarge: req.status === PAYLOAD_TOO_LARGE_STATUS,
          });
        };

        req.upload.onprogress = (event) => {
          if (event.lengthComputable && onProgress) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };

        req.onload = () => {
          if (req.status !== 200) {
            resolveWithFailure();

            return;
          }

          try {
            const { id, assemblyId } = JSON.parse(req.responseText) as {
              id: number;
              assemblyId: string;
            };

            resolve({ id, assemblyId, isFileTooLarge: false });
          } catch (_) {
            resolveWithFailure();
          }
        };

        req.onerror = resolveWithFailure;
        req.onabort = resolveWithFailure;
        req.ontimeout = resolveWithFailure;

        const formData = new FormData();

        formData.append('file', file as Blob);

        if (type === UploadType.IMAGE && imageUploadType) {
          formData.append('template', imageUploadType);
        }

        req.open(
          'POST',
          `${TCHOP_ROOT_URL}/api/fs/upload/${type}?organisation=${getOrganisationDomain()}`
        );

        prepareHeaders(new Headers()).forEach((value, key) => {
          req.setRequestHeader(key, value);
        });

        req.withCredentials = true;

        req.send(formData);
      }),
    []
  );

  return { uploadHandler, getUploadType, onUploadCompleted };
};
